import { toHTML, uriLooksSafe } from '@portabletext/to-html';

import { SanityBasicText } from './queries/basicText';

/**
 * Converts an array of Portable Text blocks into a plain text string.
 *
 * @param {SanityBasicText} blocks - The array of Portable Text blocks to convert.
 * @returns {string} The plain text representation of the blocks.
 *
 * @example
 * const blocks = [
 *   {
 *     _type: 'block',
 *     children: [{ text: 'Hello' }, { text: 'World' }],
 *   },
 *   {
 *     _type: 'block',
 *     children: [{ text: 'This' }, { text: 'is' }, { text: 'Transcend' }],
 *   },
 * ];
 * const text = blocksToText(blocks);
 * console.log(text); // Output: "HelloWorld\n\nThisisTranscend"
 */

export function blocksToText(blocks: SanityBasicText) {
  return blocks
    .map((block) => {
      if (block._type !== 'block' || !block.children) {
        return '';
      }

      return block.children.map((child) => child.text).join('');
    })
    .join('\n\n');
}

/**
 * Converts an array of Portable Text blocks into a plain text string. Only works with the 'BasicText' type from Sanity.
 *
 * @param {SanityBasicText} blocks - The array of Portable Text blocks to convert.
 * @returns {string} The plain text representation of the blocks.
 *
 * @example
 * const blocks = [
 *   {
 *     _type: 'block',
 *     children: [{ text: 'Hello' }, { text: 'World' }],
 *   },
 * ];
 * const text = blocksToUnstyledHtml(blocks);
 * console.log(text); // Output: "<p>Hello</p><p>World</p>"
 */
export function blocksToUnstyledHtmlString(blocks: SanityBasicText) {
  return toHTML(blocks, {
    components: {
      marks: {
        internalLink: ({ value, children }) => {
          if (uriLooksSafe(value.href)) {
            return `<a href="https://transcend.io${value.href}" target="_blank">${children}</a>`;
          }
          return children;
        },
        externalLink: ({ value, children }) => {
          if (uriLooksSafe(value.href)) {
            return `<a href="${value.href}" target="_blank">${children}</a>`;
          }
          return children;
        },
        anchorId: ({ children }) => {
          return children;
        },
        sup: ({ children }) => {
          return children;
        },
        sub: ({ children }) => {
          return children;
        },
      },
    },
  });
}
